import React, { useEffect } from 'react';

import { Alert, Button, Checkbox, Divider, Group, Modal, Select, Stack, Title } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useForm } from '@mantine/form';
import { useShallowEffect } from '@mantine/hooks';
import { IconAlertSquare } from '@tabler/icons-react';
import CardInfoLine from 'components/InfoCard/CardInfoLine';
import { useLocalization } from 'context/localization-context';
import dayjs from 'dayjs';
import useUpdateAthletePackageHold from 'hooks/athletePackageHold/useUpdateAthletePackageHold';
import { AthletePackageHold, AthletePackageRetrieve } from 'types';
import notificationHandler from 'utils/notifications';

interface IEditAthletePackageHoldModalProps {
  opened: boolean;
  athletePackage: AthletePackageRetrieve;
  closeHandler: () => void;
  athletePackageHold?: AthletePackageHold;
}

interface FormValues {
  athletePackageHoldId: string;
  startDate: Date | null;
  endDate: Date | null;
  reason: 'Vacation' | 'Travel' | 'Medical' | 'Financial' | 'Schedule' | 'Other' | '' | null;
  deleteUncreditedReservations: boolean;
}

const holdReasonsOptions = ['Vacation', 'Travel', 'Schedule', 'Medical', 'Financial', 'Other'];

const EditAthletePackageHoldModal = ({
  opened,
  athletePackage,
  closeHandler,
  athletePackageHold,
}: IEditAthletePackageHoldModalProps): JSX.Element => {
  const { dateFormat } = useLocalization();
  const { successNotification, errorNotification } = notificationHandler();

  const updateAthletePackageHoldMutation = useUpdateAthletePackageHold();

  const form = useForm<FormValues>({
    initialValues: {
      athletePackageHoldId: '',
      startDate: null,
      endDate: null,
      reason: null,
      deleteUncreditedReservations: true,
    },
    validate: values => ({
      startDate: values.startDate === null ? 'A start date is required' : null,
      endDate: values.endDate === null ? 'An end date is required' : null,
    }),
  });

  const handleCloseForm = (): void => {
    form.reset();
    closeHandler();
  };

  const handleSubmit = (formValues: FormValues): void => {
    const payload = {
      ...formValues,
      athletePackage: athletePackage.athletePackageId,
      startDate: dayjs(formValues.startDate).format('YYYY-MM-DD'),
      endDate: dayjs(formValues.endDate).format('YYYY-MM-DD'),
    };
    updateAthletePackageHoldMutation.mutate(payload, {
      onError: error => {
        errorNotification('Pause modification failed', error.response?.data.message);
        closeHandler();
      },
      onSuccess: () => {
        successNotification('Pause modification success', `Athlete package pause successfully updated.`);
        form.reset();
        handleCloseForm();
      },
    });
  };

  useShallowEffect(() => {
    if (!!athletePackageHold) {
      form.setValues({
        ...athletePackageHold,
        startDate: dayjs(athletePackageHold.startDate).toDate(),
        endDate: dayjs(athletePackageHold.endDate).toDate(),
        // The choice is never persisted, so every open re-asks it with the default.
        deleteUncreditedReservations: true,
      });
    }
  }, [athletePackageHold]);

  useEffect(() => {
    if (form.values.startDate && form.values.endDate && form.values.startDate > form.values.endDate) {
      form.setFieldValue('endDate', dayjs(form.values.startDate).add(1, 'd').toDate());
    }
  }, [form]);

  return (
    <Modal
      opened={opened}
      onClose={handleCloseForm}
      size='lg'
      centered
      title={
        <Title order={4} pr='md'>
          Modify Pause
        </Title>
      }
    >
      <Stack>
        <Group>
          <CardInfoLine label='Package Name' description={athletePackage.package.name} />
        </Group>
        <Group>
          <CardInfoLine
            label='Package Start and End Date'
            description={`${dayjs(athletePackage.startDate).format(dateFormat)} - ${dayjs(
              athletePackage.endDate,
            ).format(dateFormat)}`}
          />
        </Group>
        <Group>
          <CardInfoLine
            label='Current Pause Dates'
            description={`${dayjs(athletePackageHold?.startDate).format(dateFormat)} - ${dayjs(
              athletePackageHold?.endDate,
            ).format(dateFormat)}`}
          />
        </Group>
      </Stack>
      <Divider mt='sm' />
      <form onSubmit={form.onSubmit(handleSubmit)}>
        <Stack p='md'>
          <Select
            {...form.getInputProps('reason')}
            label='Pause Reason'
            placeholder='Select a reason'
            data={holdReasonsOptions}
          />
          <Group grow noWrap>
            <DatePickerInput
              {...form.getInputProps('startDate')}
              label='Begin Date'
              popoverProps={{ position: 'left', shadow: 'xl' }}
              minDate={dayjs(athletePackage.startDate).toDate()}
              maxDate={dayjs(athletePackage.endDate).toDate()}
              data-cy='holdStartDate'
              {...{ placeholder: 'Select start date' }}
            />
            <DatePickerInput
              {...form.getInputProps('endDate')}
              label='End Date'
              popoverProps={{ position: 'left', shadow: 'xl' }}
              data-cy='holdEndDate'
              minDate={dayjs(form.values.startDate).toDate()}
              {...{ placeholder: 'Select end date' }}
            />
          </Group>
          <Checkbox
            {...form.getInputProps('deleteUncreditedReservations', { type: 'checkbox' })}
            label='Also remove uncredited reservations'
            description='Extending a pause removes all reservations within the dates. Sessions already removed are not restored.'
            data-cy='holdDeleteUncreditedReservations'
          />
          <Alert variant='light' color='yellow' title='Attention' icon={<IconAlertSquare />}>
            Modifying this pause will adjust invoice due dates and credit dispensations for this package and
            any renewals. Upcoming reservations within the pause dates are removed. The pause will fail if
            credits cannot be found for any checkins linked to this package or its renewals.
          </Alert>
        </Stack>
        <Group position='right' pt='md'>
          <Button
            variant='light'
            onClick={handleCloseForm}
            size='xs'
            radius='md'
            disabled={updateAthletePackageHoldMutation.isLoading}
          >
            Cancel
          </Button>
          <Button
            size='xs'
            radius='md'
            type='submit'
            disabled={!athletePackageHold}
            loading={updateAthletePackageHoldMutation.isLoading}
            data-cy='holdsSubmit'
          >
            Save
          </Button>
        </Group>
      </form>
    </Modal>
  );
};

export default EditAthletePackageHoldModal;
